import React, { useRef } from 'react';
import { Box, Typography, makeStyles, Tooltip } from '@material-ui/core';
import AppDialog from 'components/Dialog/AppDialog';
import AppButton from 'components/CustomButtons/AppButton';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import AppInput from 'components/Input/AppInput';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showToast } from 'store/slices/toast';
import { Formik, Form, Field } from 'formik';
import SelectField from 'components/Select/SelectField';
import { FILE_STATUS } from 'config/AppConfig';
import AppColor from "utilities/AppColor";
import { maxFileSize } from 'config/AppConfig';
import moment from 'moment';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: "1rem",
  },
  fileName: {
    fontSize: 13,
    color: "#374151",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemRow: {
    padding: '10px 12px',
    marginBottom: '8px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  iconDelete: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 10,
  },
  inputField: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: '#0D9488',
    },
  },
}));

/**
 * StaffUploadTrainingModal
 *
 * Dedicated modal for training uploads/edits in ComplianceTab.
 * - Single upload mode (isMultiple = false): isShowListFile = false.
 * - Multiple upload mode (isMultiple = true): used for Other Training.
 *   Shows 3 distinct blocks: NEW, CURRENT, and PAST.
 *   - NEW block: each item has input name, expireDate (optional), file info.
 *   - CURRENT block: files with status != 'PAST'. Editable name, expireDate. Actions: delete/restore.
 *   - PAST block: files with status == 'PAST'. Editable name, expireDate. Actions: delete/restore.
 *
 * Permissions:
 * - Edit: Worker can edit local uploads and PENDING files; Admin can edit all files
 * - Delete: Worker can delete local uploads and PENDING files; Admin can delete all files
 */
const StaffUploadTrainingModal = ({
  open,
  fileTypeLabel,
  initialFile,
  initialExpiry,
  initialStatus,
  initialDeletedIds = [],
  initialServerFileEdits = {},
  item,
  items = [],
  fileNameKey,
  fileLinkKey,
  isAdmin,
  generalName,
  fileNameSelected,
  onClose,
  onConfirm,
  needsExpiry,
  autoExpiry,
  isMultiple = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const hiddenFileInputRef = useRef(null);
  // Local files state: array of objects { file, name, expireDate }
  const [localFiles, setLocalFiles] = React.useState([]);
  const [deletedIds, setDeletedIds] = React.useState([]);
  // Edits to existing server files: map of fileId -> { name, expireDate }
  const [serverFileEdits, setServerFileEdits] = React.useState({});

  // Sync localFiles on mount or initialFile change
  React.useEffect(() => {
    if (initialFile) {
      const filesArray = Array.isArray(initialFile) ? initialFile : [initialFile];
      setLocalFiles(
        filesArray
          .filter(f => f instanceof File || f?.file instanceof File)
          .map(f => {
            const rawFile = f instanceof File ? f : f.file;
            return {
              file: rawFile,
              name: f.name || rawFile.name || '',
              expireDate: f.expireDate || '',
            };
          })
      );
    } else {
      setLocalFiles([]);
    }
  }, [initialFile]);

  // Sync deletedIds on mount
  React.useEffect(() => {
    setDeletedIds(initialDeletedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync serverFileEdits on mount
  React.useEffect(() => {
    setServerFileEdits(initialServerFileEdits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── File input handlers ──────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const invalidFile = files.find(f => !ALLOWED_FILE_TYPES.includes(f.type));
    if (invalidFile) {
      dispatch(showToast({ type: 'error', message: t('validation.invalidFileImage') || 'Only PDF, Word, or Image files are allowed' }));
      return;
    }

    const oversizedFile = files.find(f => f.size > maxFileSize);
    if (oversizedFile) {
      dispatch(showToast({ type: 'error', message: t('validation.invalidSize', { maxSize: '2MB' }) }));
      return;
    }

    if (isMultiple) {
      setLocalFiles(prev => {
        const existingKeys = new Set(prev.map(f => `${f.file.name}-${f.file.size}`));
        const newItems = files
          .filter(f => !existingKeys.has(`${f.name}-${f.size}`))
          .map(f => ({
            file: f,
            name: f.name.replace(/\.[^/.]+$/, ""), // default name without extension
            expireDate: '',
          }));
        return [...newItems, ...prev];
      });
    } else {
      setLocalFiles([{
        file: files[0],
        name: files[0].name,
        expireDate: '',
      }]);
    }
  };

  const handleUpdateLocalItem = (index, field, value) => {
    setLocalFiles(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleRemoveLocalFile = (index) => {
    setLocalFiles(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Deleted-server-file handlers ────────────────────────────────────────

  const handleDeleteServerFile = (id) => {
    setDeletedIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  const handleUndeleteServerFile = (id) => {
    setDeletedIds(prev => prev.filter(existingId => existingId !== id));
  };

  // Check if current file can be deleted (If status === VERIFIED, only Admin can delete)
  const canDeleteCurrentFile = (fileItem) => {
    const fileStatus = fileItem?.status || initialStatus;
    if (isAdmin) {
      return true;
    }
    // Worker can only delete PENDING files
    if (fileStatus === FILE_STATUS.PENDING) {
      return true;
    }
    return false;
  };

  // Check if current file can be edited
  const canEditCurrentFile = (fileItem) => {
    const fileStatus = fileItem?.status || initialStatus;
    if (isAdmin) {
      return true;
    }
    // Worker can only edit PENDING files
    if (fileStatus === FILE_STATUS.PENDING) {
      return true;
    }
    return false;
  };

  // Handle edit to server file
  const handleUpdateServerFile = (fileId, field, value) => {
    setServerFileEdits(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [field]: value,
      },
    }));
  };

  // ─── Confirm ─────────────────────────────────────────────────────────────

  const handleConfirm = (values) => {
    if (isMultiple) {
      onConfirm(
        localFiles, // array of { file, name, expireDate }
        values.expiryDate,
        values.fileType,
        localFiles.length > 0 ? null : values.status,
        deletedIds,
        serverFileEdits, // map of fileId -> { name, expireDate }
      );
    } else {
      const fileToConfirm = localFiles.length > 0 ? localFiles[0].file : null;
      onConfirm(
        fileToConfirm,
        values.expiryDate,
        values.fileType,
        localFiles.length > 0 ? null : values.status,
        deletedIds,
        serverFileEdits, // map of fileId -> { name, expireDate }
      );
    }
  };

  // ─── Display helpers ──────────────────────────────────────────────────────

  const getUploadMessage = () => {
    let totalFiles = (items?.length || 0) + (localFiles.length || 0);

    if (totalFiles === 0) {
      return (generalName || fileNameSelected || <span style={{ color: "red" }}>{t('label.noFileSelected')}</span>);
    }
    if (isMultiple) {
      return `${items?.length || 0} ${items?.length === 1 ? t("label.fileAttached") : t("label.filesAttached")}${localFiles.length > 0 ? ` (${localFiles.length} ${t("label.new")})` : ''}`;
    } else {
      return localFiles.length > 0 ? localFiles[0].file.name : (fileNameSelected || generalName);
    }
  };

  const isShowExpiry = (values) => {
    if (isMultiple) return false; // In multiple mode, expiry date is per-file
    if (autoExpiry) return false;
    if (needsExpiry !== undefined) return needsExpiry;
    if (fileTypeLabel) return true;
    return false;
  };

  const uploadMessage = getUploadMessage();
  const labelUploadBtn = (item?.id && !isMultiple) || (localFiles.length === 1 && !isMultiple) ? t('button.change') : t('button.upload');

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <AppDialog
      open={open}
      title={item?.id ? t('title.editDocument') : t('title.uploadDocument')}
      onClose={onClose}
      maxWidth={isMultiple ? "md": "sm"}
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={{
            expiryDate: initialExpiry || '',
            fileType: '',
            status: initialStatus || '',
          }}
          validate={(values) => {
            const errors = {};
            if (!isMultiple && isShowExpiry(values) && !values.expiryDate) {
              errors.expiryDate = t('validation.required');
            }
            return errors;
          }}
          onSubmit={handleConfirm}
        >
          {({ values }) => {
            const showExpiry = isShowExpiry(values);
            const computedShowStatus = isAdmin && !!item?.id && !localFiles.length;
            
            return (
              <Form>
                <Typography className={classes.sectionTitle}>
                  {t('clientDocument.sectionDocument')}
                </Typography>

                <Box className={classes.fileBox} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444" }} />
                    <Box className={classes.fileName} style={{ flex: 1 }}>
                      {uploadMessage}
                    </Box>
                    <AppButton
                      label={labelUploadBtn}
                      mode="outlined"
                      rounder
                      style={{ fontSize: 11, textTransform: "none", height: 28 }}
                      onClick={() => {
                        if (hiddenFileInputRef.current) {
                          hiddenFileInputRef.current.value = null;
                          hiddenFileInputRef.current.click();
                        }
                      }}
                    />
                  </Box>

                  {/* ShowListFile UI: Applies ONLY for isMultiple (Other Training) */}
                  {isMultiple && (items.length > 0 || localFiles.length > 0) && (
                    <Box style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Block 1: NEW Files (Local Uploads with per-file Inputs) */}
                      {localFiles.length > 0 && (
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#059669', marginBottom: 6, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                            {t("label.newVersion", "New Files")}
                          </Typography>
                          {localFiles.map((localItem, index) => (
                            <Box key={`local-${index}`} className={classes.itemRow} style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <i
                                  className="fas fa-trash"
                                  style={{ fontSize: 14, color: "#EF4444", cursor: 'pointer' }}
                                  onClick={() => handleRemoveLocalFile(index)}
                                />
                              </Box>

                              <div className="row">
                                <div className="col-sm-12 col-md-6 mb-2">
                                  <AppInput
                                    label={t("label.name")}
                                    id={`local-name-${index}`}
                                    placeholder={t("label.namePlaceholder", "Enter name")}
                                    field={{
                                      name: `local-name-${index}`,
                                      value: localItem.name,
                                      onChange: (e) => handleUpdateLocalItem(index, 'name', e.target.value)
                                    }}
                                    required
                                    form={{ errors: {}, touched: {} }}
                                    onChange={(val) => handleUpdateLocalItem(index, 'name', val)}
                                  />
                                </div>
                                <div className="col-sm-12 col-md-6 mb-2">
                                  <CustomDatePicker
                                    label={t("label.expiryDate")}
                                    id={`local-expiry-${index}`}
                                    formatDateJob={true}
                                    field={{
                                      name: `local-expiry-${index}`,
                                      value: localItem.expireDate,
                                      onChange: (e) => handleUpdateLocalItem(index, 'expireDate', e.target.value)
                                    }}
                                    form={{ errors: {}, touched: {} }}
                                    onChange={(val) => handleUpdateLocalItem(index, 'expireDate', val)}
                                    classInput={'date-picker-container-custom'}
                                  />
                                </div>
                                <div className="col-sm-12 col-md-12 mt-1">
                                  <Box style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <i className="fas fa-paperclip" style={{ fontSize: 11 }} />
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {localItem.file.name}
                                    </span>
                                  </Box>
                                </div>
                              </div>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Block 2: CURRENT Files (Server Files with status != PAST) - Editable with Delete/Restore */}
                      {(() => {
                        const currentItems = items.filter(it => (it.status || '') !== FILE_STATUS.PAST);
                        return currentItems.length > 0 ? (
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#0D9488', marginBottom: 4, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                            {t("label.currentVersion")}
                          </Typography>
                          {currentItems.map((serverItem, index) => {
                            const edits = serverFileEdits[serverItem.id] || {};
                            const currentName = edits.name !== undefined ? edits.name : (
                              serverItem[fileNameKey]
                              || serverItem.fileName
                              || serverItem.generalName
                              || serverItem[fileLinkKey]?.split('/').pop()
                              || ''
                            );
                            const currentExpiry = edits.expireDate !== undefined ? edits.expireDate : (
                              serverItem.expireDate || serverItem.expiryDate || ''
                            );
                            const serverOriginalFileName = serverItem.otherFile || serverItem.generalName || serverItem.fileName || '';
                            const isDeleted = deletedIds.includes(serverItem.id);
                            const allowDelete = canDeleteCurrentFile(serverItem);
                            const allowEdit = canEditCurrentFile(serverItem);

                            return (
                              <Box
                                key={`server-current-${serverItem.id || index}`}
                                className={classes.itemRow}
                                style={{
                                  backgroundColor: isDeleted ? '#FEE2E2' : '#F0FDFA',
                                  borderColor: isDeleted ? '#FCA5A5' : '#CCFBF1',
                                  opacity: isDeleted ? 0.6 : 1,
                                }}
                              >
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 6, position: 'relative' }}>
                                  <Box className={classes.iconDelete}>
                                    {isDeleted ? (
                                      <Tooltip title={t('button.restore', 'Restore')}>
                                        <i
                                          className="fas fa-undo"
                                          style={{ fontSize: 14, color: "#059669", cursor: 'pointer' }}
                                          onClick={() => handleUndeleteServerFile(serverItem.id)}
                                        />
                                      </Tooltip>
                                    ) : (
                                      allowDelete ? (
                                        <Tooltip title={t('button.delete', 'Delete')}>
                                          <i
                                            className="fas fa-trash"
                                            style={{ fontSize: 14, color: "#EF4444", cursor: 'pointer' }}
                                            onClick={() => handleDeleteServerFile(serverItem.id)}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <i className="fas fa-lock" style={{ fontSize: 12, color: "#9CA3AF" }} />
                                      )
                                    )}
                                  </Box>
                                </Box>

                                <div className="row">
                                  <div className="col-sm-12 col-md-6 mb-2">
                                    {allowEdit && !isDeleted ? (
                                      <AppInput
                                        label={t("label.name")}
                                        id={`current-name-${serverItem.id}`}
                                        placeholder={t("label.namePlaceholder", "Enter name")}
                                        field={{
                                          name: `current-name-${serverItem.id}`,
                                          value: currentName,
                                          onChange: (e) => handleUpdateServerFile(serverItem.id, 'name', e.target.value)
                                        }}
                                        form={{ errors: {}, touched: {} }}
                                        onChange={(val) => handleUpdateServerFile(serverItem.id, 'name', val)}
                                      />
                                    ) : (
                                      <>
                                        <span style={{ fontSize: 12, color: '#6B7280', display: 'block', marginRight: 4 }}>{t("label.fileName", "Document Name")}:</span>
                                        <span style={{
                                          fontSize: 12, fontWeight: 600, color: isDeleted ? '#991B1B' : '#374151',
                                          textDecoration: isDeleted ? 'line-through' : 'none',
                                        }}>
                                          {currentName}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <div className="col-sm-12 col-md-6 mb-2">
                                    {allowEdit && !isDeleted ? (
                                      <CustomDatePicker
                                        label={t("label.expiryDate")}
                                        id={`current-expiry-${serverItem.id}`}
                                        formatDateJob={true}
                                        field={{
                                          name: `current-expiry-${serverItem.id}`,
                                          value: currentExpiry,
                                          onChange: (e) => handleUpdateServerFile(serverItem.id, 'expireDate', e.target.value)
                                        }}
                                        form={{ errors: {}, touched: {} }}
                                        onChange={(val) => handleUpdateServerFile(serverItem.id, 'expireDate', val)}
                                        classInput={'date-picker-container-custom'}
                                      />
                                    ) : (
                                      <>
                                        <span style={{ fontSize: 12, color: '#6B7280', display: 'block', marginRight: 4 }}>{t("label.expiryDate")}:</span>
                                        <span style={{
                                          fontSize: 12, fontWeight: 600, color: isDeleted ? '#991B1B' : '#374151',
                                          textDecoration: isDeleted ? 'line-through' : 'none',
                                        }}>
                                          {currentExpiry ? moment.utc(currentExpiry).format('DD/MM/YYYY') || '-' : '-'}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {serverOriginalFileName && (
                                    <div className="col-sm-12 col-md-12 mt-1">
                                      <Box style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <i className="fas fa-paperclip" style={{ fontSize: 11 }} />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {serverOriginalFileName}
                                        </span>
                                      </Box>
                                    </div>
                                  )}
                                </div>
                              </Box>
                            );
                          })}
                        </Box>
                        ) : null;
                      })()}

                      {/* Block 3: PAST Files (Server Files with status == PAST) - Editable with Delete/Restore */}
                      {(() => {
                        const pastItems = items.filter(it => (it.status || '') === FILE_STATUS.PAST);
                        return pastItems.length > 0 ? (
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#6B7280', marginBottom: 4, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                            {t("label.pastVersions")}
                          </Typography>
                          {pastItems.map((serverItem, index) => {
                            const edits = serverFileEdits[serverItem.id] || {};
                            const currentName = edits.name !== undefined ? edits.name : (
                              serverItem[fileNameKey]
                              || serverItem.fileName
                              || serverItem.generalName
                              || serverItem[fileLinkKey]?.split('/').pop()
                              || ''
                            );
                            const currentExpiry = edits.expireDate !== undefined ? edits.expireDate : (
                              serverItem.expireDate || serverItem.expiryDate || ''
                            );
                            const serverOriginalFileName = serverItem.otherFile || serverItem.generalName || serverItem.fileName || '';
                            const isDeleted = deletedIds.includes(serverItem.id);
                            const allowDelete = canDeleteCurrentFile(serverItem);
                            const allowEdit = canEditCurrentFile(serverItem);

                            return (
                              <Box
                                key={`server-past-${serverItem.id || index}`}
                                className={classes.itemRow}
                                style={{
                                  backgroundColor: isDeleted ? '#FEE2E2' : '#F9FAFB',
                                  borderColor: isDeleted ? '#FCA5A5' : '#E5E7EB',
                                  opacity: isDeleted ? 0.6 : 1,
                                }}
                              >
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 6, position: 'relative' }}>
                                  <Box className={classes.iconDelete}>
                                    {isDeleted ? (
                                      <Tooltip title={t('button.restore', 'Restore')}>
                                        <i
                                          className="fas fa-undo"
                                          style={{ fontSize: 14, color: "#059669", cursor: 'pointer' }}
                                          onClick={() => handleUndeleteServerFile(serverItem.id)}
                                        />
                                      </Tooltip>
                                    ) : (
                                      allowDelete ? (
                                        <Tooltip title={t('button.delete', 'Delete')}>
                                          <i
                                            className="fas fa-trash"
                                            style={{ fontSize: 14, color: "#EF4444", cursor: 'pointer' }}
                                            onClick={() => handleDeleteServerFile(serverItem.id)}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <i className="fas fa-lock" style={{ fontSize: 12, color: "#9CA3AF" }} />
                                      )
                                    )}
                                  </Box>
                                </Box>

                                <div className="row">
                                  <div className="col-sm-12 col-md-6 mb-2">
                                    {allowEdit && !isDeleted ? (
                                      <AppInput
                                        label={t("label.name")}
                                        id={`past-name-${serverItem.id}`}
                                        placeholder={t("label.namePlaceholder", "Enter name")}
                                        field={{
                                          name: `past-name-${serverItem.id}`,
                                          value: currentName,
                                          onChange: (e) => handleUpdateServerFile(serverItem.id, 'name', e.target.value)
                                        }}
                                        form={{ errors: {}, touched: {} }}
                                        onChange={(val) => handleUpdateServerFile(serverItem.id, 'name', val)}
                                      />
                                    ) : (
                                      <>
                                        <span style={{ fontSize: 12, color: '#6B7280', display: 'block', marginRight: 4 }}>{t("label.fileName", "Document Name")}:</span>
                                        <span style={{
                                          fontSize: 12, fontWeight: 600, color: isDeleted ? '#991B1B' : '#374151',
                                          textDecoration: isDeleted ? 'line-through' : 'none',
                                        }}>
                                          {currentName}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <div className="col-sm-12 col-md-6 mb-2">
                                    {allowEdit && !isDeleted ? (
                                      <CustomDatePicker
                                        label={t("label.expiryDate")}
                                        id={`past-expiry-${serverItem.id}`}
                                        formatDateJob={true}
                                        field={{
                                          name: `past-expiry-${serverItem.id}`,
                                          value: currentExpiry,
                                          onChange: (e) => handleUpdateServerFile(serverItem.id, 'expireDate', e.target.value)
                                        }}
                                        form={{ errors: {}, touched: {} }}
                                        onChange={(val) => handleUpdateServerFile(serverItem.id, 'expireDate', val)}
                                        classInput={'date-picker-container-custom'}
                                      />
                                    ) : (
                                      <>
                                        <span style={{ fontSize: 12, color: '#6B7280', display: 'block', marginRight: 4 }}>{t("label.expiryDate")}:</span>
                                        <span style={{
                                          fontSize: 12, fontWeight: 600, color: isDeleted ? '#991B1B' : '#374151',
                                          textDecoration: isDeleted ? 'line-through' : 'none',
                                        }}>
                                          {currentExpiry ? moment.utc(currentExpiry).format('DD/MM/YYYY') || '-' : '-'}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {serverOriginalFileName && (
                                    <div className="col-sm-12 col-md-12 mt-1">
                                      <Box style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <i className="fas fa-paperclip" style={{ fontSize: 11 }} />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {serverOriginalFileName}
                                        </span>
                                      </Box>
                                    </div>
                                  )}
                                </div>
                              </Box>
                            );
                          })}
                        </Box>
                        ) : null;
                      })()}
                    </Box>
                  )}

                  <input
                    accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: 'none' }}
                    id="upload-training-modal-file-input"
                    type="file"
                    multiple={isMultiple}
                    ref={hiddenFileInputRef}
                    onChange={handleFileChange}
                  />
                </Box>

                {!isMultiple && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      {t('clientDocument.sectionDetails')}
                    </Typography>

                    <div className="row">
                      <div className="col-sm-12 col-md-12 mb-3">
                        <label style={{ paddingTop: 0 }}>{t('label.fileType')}</label>
                        <Typography variant="body2">{fileTypeLabel}</Typography>
                      </div>

                      {showExpiry && (
                        <div className="col-sm-12 col-md-6">
                          <Field
                            component={CustomDatePicker}
                            label={t('label.expiryDate')}
                            name="expiryDate"
                            type="text"
                            id="modalExpiryDate"
                            formatDateJob={true}
                            required
                            classInput={'date-picker-container-custom'}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {computedShowStatus && (
                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={SelectField}
                        name="status"
                        label={t('label.status')}
                        options={[
                          { value: FILE_STATUS.PENDING, label: t('fileStatus.PENDING') },
                          { value: FILE_STATUS.VERIFIED, label: t('fileStatus.VERIFIED') },
                        ]}
                        placeholder={t('label.selectStatus')}
                      />
                    </div>
                  </div>
                )}

                <Box className={classes.control}>
                  <AppButton
                    label={t("button.cancel")}
                    onClick={onClose}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                  />
                  <AppButton
                    label={t("button.confirm")}
                    rounder
                    type="submit"
                    disabled={(localFiles.length === 0 && deletedIds.length === 0 && Object.keys(serverFileEdits).length === 0 && !initialExpiry && !initialStatus)}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default StaffUploadTrainingModal;
